import {
  type ComponentProps,
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useEffectEvent,
  useState,
} from 'react'
import { forwardRef, startTransition, useCallback } from 'react'
import { clsx } from 'clsx'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { type DialogContentProps } from '@radix-ui/react-dialog'
import { contentWrapper, dialogWindow, overlay } from './Dialog.css'

type ContentProps = {
  children: ReactNode
  onClose?: () => void
  className?: string
  frostedOverlay?: boolean
  scrollableOverlay?: boolean
  overlayClassName?: string
  centerContent?: boolean
  closeOnPointerDownOutside?: boolean
  asChild?: boolean
}

const Content = (props: ContentProps) => {
  const handleClose = () => props.onClose?.()

  const handleInteractOutside = (event: Parameters<NonNullable<DialogContentProps['onInteractOutside']>>[0]) => {
    if (event.type !== 'dismissableLayer.pointerDownOutside' || !props.closeOnPointerDownOutside) {
      event.preventDefault()
    }
  }

  const innerContent = (
    <DialogPrimitive.Content
      className={props.className}
      onEscapeKeyDown={handleClose}
      onInteractOutside={handleInteractOutside}
      // Prevents accessibility warning from radix
      // Adding non-visible descriptions everywhere is not a priority right now
      aria-describedby={undefined}
      asChild={props.asChild}
    >
      {props.children}
    </DialogPrimitive.Content>
  )

  return (
    <DialogPrimitive.Portal>
      {props.scrollableOverlay ? (
        <Overlay className={clsx(overlay.scrollable, props.overlayClassName)} frosted={props.frostedOverlay}>
          {innerContent}
        </Overlay>
      ) : (
        <>
          <Overlay frosted={props.frostedOverlay} />
          <div className={clsx(contentWrapper.base, props.centerContent && contentWrapper.centered)}>
            {innerContent}
          </div>
        </>
      )}
    </DialogPrimitive.Portal>
  )
}

type OverlayProps = {
  frosted?: boolean
  className?: string
}
const Overlay = forwardRef<HTMLDivElement, PropsWithChildren<OverlayProps>>(({ className, frosted, children }, ref) => {
  return (
    <DialogPrimitive.Overlay ref={ref} className={clsx(overlay.base, frosted && overlay.frosted, className)}>
      {children}
    </DialogPrimitive.Overlay>
  )
})
Overlay.displayName = 'Overlay'

const Root = ({
  // Animation assistance option
  // If specified, dialog transition through (open=true, IsClosingContext.value=true) state
  // before settling in (open=false, IsClosingContext.value=false)
  closingDuration = 0,
  open = false,
  onOpenChange,
  children,
  ...forwardedProps
}: ComponentProps<typeof DialogPrimitive.Root> & { closingDuration?: number }) => {
  // Optimization: dialogs can get quite large, so it's good idea to treat opening/closing as transition
  // We're creating local state and maintaining it for both controlled dialogs (effect)
  // and uncontrolled ones (handleOpenChange)
  const [internalOpen, setInternalOpen] = useState(forwardedProps.defaultOpen ?? false)
  const setInternalOpenEffectEvent = useEffectEvent(setInternalOpen)
  // NOTE: No animation delay here, we can add this if it ever becomes a problem
  // Assuming new `open` value changes as a result of handleOpenChange, should be fine
  useEffect(() => setInternalOpenEffectEvent(open), [open])

  const [isClosing, setIsClosing] = useState(false)
  const handleOpenChange = useCallback(
    (newValue: boolean) => {
      startTransition(() => {
        if (internalOpen && !newValue && closingDuration > 0) {
          setIsClosing(true)
          setTimeout(() => {
            setIsClosing(false)
            setInternalOpen(newValue)
            onOpenChange?.(newValue)
          }, closingDuration)
        } else {
          setInternalOpen(newValue)
          onOpenChange?.(newValue)
        }
      })
    },
    [closingDuration, internalOpen, onOpenChange],
  )
  return (
    <DialogPrimitive.Root open={internalOpen} onOpenChange={handleOpenChange} {...forwardedProps}>
      <IsClosingContext.Provider value={isClosing}>{children}</IsClosingContext.Provider>
    </DialogPrimitive.Root>
  )
}

const IsClosingContext = createContext(false)
IsClosingContext.displayName = 'IsClosingContext'

export const Trigger = DialogPrimitive.Trigger
export const Title = DialogPrimitive.Title
export const Close = DialogPrimitive.Close

// Example of exposing base component for further styling by either vanilla-extract or emotion
const Window = ({ className, ...forwardedProps }: { className?: string } & PropsWithChildren) => (
  <div className={clsx(dialogWindow, className)} {...forwardedProps} />
)

export const Dialog = {
  Content,
  Close: DialogPrimitive.Close,
  IsClosingContext,
  Root,
  Title: DialogPrimitive.Title,
  Trigger: DialogPrimitive.Trigger,
  Window,
}
